import { connect } from "react-redux";
import React from "react";
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import { fetchAllEncounters, fetchAllTestEncounters, postTestEncounter } from "@/shared/encounter/actions";
import EncounterList from "@/components/EncounterList";
import {isTopNavEnabled} from "../../shared/access";

const mapStateToProps = state => ({
    lastNewEncounterAt: state.encounter.lastNewEncounterAt,
    ...state
})

const mapDispatchToProps = dispatch => ({
    getEncounterPage: (page) => dispatch(fetchAllEncounters(page)),
    getTestEncounters: (page) => dispatch(fetchAllTestEncounters(page)),
    createTestEncounter: (testEncounter) => dispatch(postTestEncounter(testEncounter))
})

const RELOAD_RESET_TIME = 15000;

function ActivityEncounterList({ lastNewEncounterAt, ...props }) {
    const [encounters, setEncounters] = React.useState([]);
    const [activePage, setActivePage] = React.useState({});
    const [page, setPage] = React.useState(0);
    const [searchFocused, setSearchFocused] = React.useState(false);
    const [searchVal, setSearchVal] = React.useState("");
    const [searchInput, setSearchInput] = React.useState("");
    const [isReloadEnabled, setReloadEnabled] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSearchInProgress, setIsSearchInProgress] = React.useState(false);
    const searchRef = React.createRef();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => loadEncounters(), []);

    React.useEffect(() => {
        // Reload when there is a new encounter event
        if (!searchVal && isReloadEnabled && lastNewEncounterAt)
            loadEncounters(false, page, searchRef.current.value, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastNewEncounterAt])

    const loadFirstPage = (searchParam) => {
        // Block if search is in progress and searchParam is not null/empty
        if (isSearchInProgress && searchParam && searchParam.trim() !== "") {
            return;
        }

        setPage(0);
        loadEncounters(true, 0, searchParam);
    }

    const loadEncounters = (isSearch = false, pageNum = 0, searchParam = "", resetReload = false) => {
        // Block if search is in progress and searchParam is not null/empty
        if (isSearchInProgress && searchParam && searchParam.trim() !== "") {
            return;
        }

        if (resetReload) setReloadEnabled(false);

        // Set loading states
        setIsLoading(true);
        if (searchParam && searchParam.trim() !== "") {
            setIsSearchInProgress(true);
        }

        API.get('Core', '/api/v1/encounter/page', {
            queryStringParameters: {
                page: pageNum,
                search: searchParam
            }
        })
            .then(response => {
                    // if Searching then just set the content
                    if (isSearch) setEncounters(response.content);
                    else setEncounters(encounters.concat(response.content));

                    setActivePage(response);
                },
                error => {
                    toast("Could not load survey results: " + error.response.data.message, { position: "top-center", type: "error" });
                }
            ).finally(() => {
            // Reset loading states
            setIsLoading(false);
            setIsSearchInProgress(false);

            if (resetReload) setTimeout(() => setReloadEnabled(true), RELOAD_RESET_TIME);
        });
    }

    const loadMore = () => {
        let nextPage = page + 1;
        setPage(nextPage);
        loadEncounters(false, nextPage, searchRef.current.value);
    }

    const onTestEncounterSubmit = async (testEncounter) => {
        await props.createTestEncounter(testEncounter);
        loadEncounters(false, page, searchRef.current?.value ? searchRef.current?.value : "");
    }

    const keyPress = (e) => {
        if (e.keyCode === 13) {
            const searchValue = searchRef.current.value;

            // Block if search is in progress and search value is not null/empty
            if (isSearchInProgress && searchValue && searchValue.trim() !== "") {
                return;
            }

            setSearchVal(searchValue);
            loadFirstPage(searchValue);
        }
    }

    const clearSearch = () => {
        setSearchInput("");
        setSearchVal("");
        loadFirstPage("");
    }

    const onSearchFocused = () => {
        setSearchFocused(true);
    }

    const onSearchBlurred = () => {
        setSearchFocused(false);
    }

    const onSearchChange = (e) => {
        setSearchInput(searchRef.current.value);
    }

    return (
        <div>
            <div className="secondaryNavBar">
                <div className="navTitle">
                    <div className="viewName">All Encounters</div>
                    <div className="counterBadge">
                        <div className="counterValue">{activePage ? activePage.totalElements : ''}</div>
                    </div>
                </div>

                <div className="navActions">
                    {/*<TestEncounter encounters={props.encounter.allTestEncounters} loadEncounters={props.getTestEncounters} onSend={onTestEncounterSubmit} />*/}

                    <div className={`searchInput ${searchFocused ? "isFocused" : ""} ${(searchInput.length > 0) ? "hasInput" : ""} ${isSearchInProgress ? "isSearching" : ""}`}>
                        <div className="icon" />
                        <input
                            value={searchInput}
                            onChange={onSearchChange}
                            placeholder={"Search Incident Number"}
                            onFocus={onSearchFocused}
                            onBlur={onSearchBlurred}
                            className="input"
                            onKeyDown={keyPress}
                            type="text"
                            ref={searchRef}
                            disabled={isSearchInProgress && searchInput && searchInput.trim() !== ""}
                        />
                        <div className="clear" onClick={clearSearch}></div>
                        {isSearchInProgress && (
                            <div className="searchLoader">
                                <div
                                    className="blueLoader small"
                                    role="status"
                                    aria-label="Searching encounters"
                                    aria-live="polite"
                                ></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main loading overlay */}
            {isLoading && (
                <div
                    className="loadingOverlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="loading-title"
                >
                    <div className="loadingCard">
                        <div
                            className="blueLoader large"
                            role="status"
                            aria-label="Loading encounters"
                            aria-live="polite"
                        ></div>
                        <div id="loading-title" className="loadingText">Loading encounters...</div>
                    </div>
                </div>
            )}

            <div className={`activitySection sectionAllEncoutners ${isTopNavEnabled() ? 'withTopNav' : ''} ${isLoading ? 'isLoading' : ''}`}>
                <EncounterList includeEncounterDetailsModal={true} title="" encounters={encounters} />
                {
                    (activePage && !activePage.last && !(activePage.numberOfElements < 20)) ? (
                        <div onClick={loadMore} className="encountersExpander">
                            <div className="expanderButton">
                                <div className="title">Show More</div>
                                <div className="icon" />
                            </div>
                        </div>
                    ) : null
                }
            </div>
        </div>
    )
}

export default connect(mapStateToProps, mapDispatchToProps)(ActivityEncounterList);